import { createSlice } from "@reduxjs/toolkit";
import {
  fetchQuotations,
  fetchQuotationById,
  createQuotation,
  updateQuotation,
  deleteQuotation,
  updateQuotationStatus,
  fetchQuotationDropdowns,
} from "./thunk";

interface QuotationState {
  quotationList: any[];
  rowCount: number;
  loading: boolean;
  error: string | null;
  selectedQuotation: any;
  dropdowns: {
    [key: string]: any[];
  };
}

const initialState: QuotationState = {
  quotationList: [],
  rowCount: 0,
  loading: false,
  error: null,
  selectedQuotation: null,
  dropdowns: {},
};

const quotationSlice = createSlice({
  name: "quotation",
  initialState,
  reducers: {
    clearSelectedQuotation: (state) => {
      state.selectedQuotation = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch quotations
      .addCase(fetchQuotations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuotations.fulfilled, (state, action) => {
        state.loading = false;
        state.quotationList = action.payload.data || [];
        state.rowCount = action.payload.rowCount || 0;
      })
      .addCase(fetchQuotations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Failed to fetch quotations";
        state.quotationList = [];
      })
      // Fetch quotation by ID
      .addCase(fetchQuotationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuotationById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedQuotation = action.payload;
      })
      .addCase(fetchQuotationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Failed to fetch quotation";
      })
      // Create quotation
      .addCase(createQuotation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createQuotation.fulfilled, (state, action) => {
        state.loading = false;
        // Add newly created quotation to the list if needed
        if (action.payload) {
          state.quotationList = [action.payload, ...state.quotationList];
          state.rowCount = state.rowCount + 1;
        }
      })
      .addCase(createQuotation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Failed to create quotation";
      })
      // Update quotation
      .addCase(updateQuotation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateQuotation.fulfilled, (state, action) => {
        state.loading = false;
        // Update the quotation in the list if it exists
        const updatedQuotation = action.payload;
        if (updatedQuotation?.id || updatedQuotation?.quotation_id) {
          const quotationId = updatedQuotation.id || updatedQuotation.quotation_id;
          state.quotationList = state.quotationList.map((quotation) =>
            (quotation.id === quotationId || quotation.quotation_id === quotationId)
              ? { ...quotation, ...updatedQuotation }
              : quotation
          );
          // Update selected quotation if it's the same
          if (state.selectedQuotation && 
              (state.selectedQuotation.id === quotationId || state.selectedQuotation.quotation_id === quotationId)) {
            state.selectedQuotation = updatedQuotation;
          }
        }
      })
      .addCase(updateQuotation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Failed to update quotation";
      })
      // Delete quotation
      .addCase(deleteQuotation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteQuotation.fulfilled, (state, action) => {
        state.loading = false;
        // Remove deleted quotation from the list
        const deletedId = action.payload?.id || action.payload?.quotation_id;
        if (deletedId) {
          state.quotationList = state.quotationList.filter(
            (quotation) => quotation.id !== deletedId && quotation.quotation_id !== deletedId
          );
          state.rowCount = Math.max(0, state.rowCount - 1);
        }
        // Clear selected quotation if it was deleted
        if (state.selectedQuotation && 
            (state.selectedQuotation.id === deletedId || state.selectedQuotation.quotation_id === deletedId)) {
          state.selectedQuotation = null;
        }
      })
      .addCase(deleteQuotation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Failed to delete quotation";
      })
      // Update quotation status
      .addCase(updateQuotationStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateQuotationStatus.fulfilled, (state, action) => {
        state.loading = false;
        // Update the quotation status in the list if it exists
        const updatedQuotation = action.payload;
        if (updatedQuotation?.id || updatedQuotation?.quotation_id) {
          const quotationId = updatedQuotation.id || updatedQuotation.quotation_id;
          state.quotationList = state.quotationList.map((quotation) =>
            (quotation.id === quotationId || quotation.quotation_id === quotationId)
              ? { ...quotation, ...updatedQuotation }
              : quotation
          );
        }
      })
      .addCase(updateQuotationStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Failed to update quotation status";
      })
      // Fetch dropdowns
      .addCase(fetchQuotationDropdowns.pending, (state) => {
        // Optionally set loading state for dropdowns
      })
      .addCase(fetchQuotationDropdowns.fulfilled, (state, action) => {
        state.dropdowns[action.payload.columnId] = action.payload.data;
      })
      .addCase(fetchQuotationDropdowns.rejected, (state) => {
        // Handle dropdown fetch errors if needed
      });
  },
});

export const { clearSelectedQuotation, clearError } = quotationSlice.actions;
export default quotationSlice.reducer;

